/**
 * The second tier of the read (SYR-111 v4): everything behind the headlines,
 * folded away for the reader who only wants the headlines and the fixes, one
 * click for the analyst who wants all of it. Nothing inside renders while it
 * is closed, so the heavy tables cost nothing until asked for.
 */

import React from 'react';
import { useIntl } from 'react-intl';
import { THEME } from '../../config/theme';

interface Props {
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

export const AllTheData: React.FunctionComponent<Props> = ({ open, onToggle, children }) => {
  const intl = useIntl();
  return (
    <section id="all-the-data" style={{ paddingTop: 44 }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          borderTop: `1px solid ${THEME.border}`,
          paddingTop: 28,
        }}
      >
        <div style={{ minWidth: 0, maxWidth: '72ch' }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: THEME.textMuted, marginBottom: 6 }}>
            {intl.formatMessage({
              id: 'marketerView.modelBeliefs.allData.eyebrow',
              defaultMessage: 'All of the data',
            })}
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: THEME.textBase, lineHeight: 1.25, marginBottom: 6 }}>
            {intl.formatMessage({
              id: 'marketerView.modelBeliefs.allData.title',
              defaultMessage: 'Everything behind the headlines',
            })}
          </div>
          <div style={{ fontSize: 14, color: THEME.textSecondary, lineHeight: 1.55 }}>
            {intl.formatMessage({
              id: 'marketerView.modelBeliefs.allData.sub',
              defaultMessage:
                'How the numbers were made, the full rank table, what the models read and what those pages say, every statement with the models’ reasons, the pages behind each one, your customers’ answers, and the change over time.',
            })}
          </div>
        </div>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls="all-the-data-body"
          style={{
            background: open ? '#fff' : THEME.primary,
            color: open ? THEME.textBase : '#fff',
            border: open ? `1px solid ${THEME.border}` : `1px solid ${THEME.primary}`,
            borderRadius: 10,
            padding: '10px 18px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            flex: '0 0 auto',
          }}
        >
          {open
            ? intl.formatMessage({
                id: 'marketerView.modelBeliefs.allData.hide',
                defaultMessage: 'Hide all of the data',
              })
            : intl.formatMessage({
                id: 'marketerView.modelBeliefs.allData.show',
                defaultMessage: 'Show all of the data',
              })}
        </button>
      </div>
      {open && <div id="all-the-data-body">{children}</div>}
    </section>
  );
};
